import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { errMsg, getToken, WS_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageSquare, Send, ArrowLeft, Loader2, Package } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Messages() {
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const wsRef = useRef(null);
  const endRef = useRef(null);

  const loadConvos = useCallback(async () => {
    try { const { data } = await api.get("/conversations"); setConvos(data.conversations); return data.conversations; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openConvo = async (c) => {
    setActive(c);
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/messages/${c.id}`);
      setMessages(data.messages);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoadingMsgs(false); }

    // websocket
    if (wsRef.current) { try { wsRef.current.close(); } catch {} }
    const ws = new WebSocket(`${WS_BASE}/ws/${c.id}?token=${encodeURIComponent(getToken())}`);
    ws.onmessage = (ev) => {
      try {
        const payload = JSON.parse(ev.data);
        if (payload.type === "message") {
          setMessages((m) => (m.some((x) => x.id === payload.message.id) ? m : [...m, payload.message]));
        }
      } catch {}
    };
    wsRef.current = ws;
  };

  useEffect(() => () => { if (wsRef.current) try { wsRef.current.close(); } catch {} }, []);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || !active) return;
    setText("");
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ text: t }));
    } else {
      try {
        const { data } = await api.post(`/messages/${active.id}`, { text: t });
        setMessages((m) => [...m, data.message]);
      } catch (e) { toast.error(errMsg(e)); }
    }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6">Messages</h1>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div> : convos.length === 0 ? (
        <EmptyState icon={MessageSquare} title="No conversations yet." description="Chats become available once you and another user accept a connection." testid="empty-messages" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-[calc(100vh-14rem)] min-h-[420px]">
          {/* Conversation list */}
          <div className={cn("lg:col-span-4 rounded-2xl border border-slate-200 bg-white overflow-y-auto pm-scroll", active && "hidden lg:block")} data-testid="conversation-list">
            {convos.map((c) => (
              <button key={c.id} onClick={() => openConvo(c)} data-testid={`conversation-${c.id}`}
                className={cn("w-full flex items-center gap-3 p-4 border-b border-slate-100 text-left hover:bg-accent/50", active?.id === c.id && "bg-accent")}>
                <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-primary font-semibold flex-shrink-0">{c.other_user?.name?.[0] || "?"}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{c.other_user?.name}</p>
                  <p className="text-xs text-slate-400 truncate">{c.last_message?.text || c.delivery?.description}</p>
                </div>
                {c.unread > 0 && <span className="w-5 h-5 rounded-full bg-primary text-white text-[10px] flex items-center justify-center">{c.unread}</span>}
              </button>
            ))}
          </div>

          {/* Chat window */}
          <div className={cn("lg:col-span-8 rounded-2xl border border-slate-200 bg-white flex flex-col", !active && "hidden lg:flex")}>
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select a conversation to start chatting</div>
            ) : (
              <>
                <div className="flex items-center gap-3 p-4 border-b border-slate-100">
                  <button className="lg:hidden" onClick={() => setActive(null)} data-testid="chat-back-btn"><ArrowLeft className="w-5 h-5 text-slate-500" /></button>
                  <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-primary font-semibold">{active.other_user?.name?.[0]}</div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{active.other_user?.name}</p>
                    <p className="text-xs text-slate-400 flex items-center gap-1 truncate"><Package className="w-3 h-3" /> {active.delivery?.description}</p>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 pm-scroll" data-testid="chat-messages">
                  {loadingMsgs ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div> :
                    messages.length === 0 ? <p className="text-center text-sm text-slate-400 py-6">No messages yet. Say hello 👋</p> :
                    messages.map((m) => {
                      const mine = m.sender_id === user.id;
                      return (
                        <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                          <div className={cn("max-w-[75%] rounded-2xl px-4 py-2", mine ? "bg-primary text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm")}>
                            <p className="text-sm break-words">{m.text}</p>
                            <p className={cn("text-[10px] mt-1", mine ? "text-white/70" : "text-slate-400")}>{fmt(m.created_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                  <div ref={endRef} />
                </div>
                <form onSubmit={send} className="p-3 border-t border-slate-100 flex gap-2">
                  <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="rounded-full" />
                  <Button type="submit" size="icon" className="rounded-full flex-shrink-0" data-testid="chat-send-btn"><Send className="w-4 h-4" /></Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
